/**
 * ContractDetail — read-only preview of an OCR'd contract, with the demo's
 * "Potvrdit" button that triggers the R-001-disappears moment back on the
 * scene.
 *
 * Why look the contract up via `useContracts()` and not pass it as state?
 * Two reasons:
 *   1. The MSW handlers are the source of truth (they own the in-memory store
 *      that mutates on confirm). Re-querying keeps us honest about that.
 *   2. A hard refresh on `/contract/c-123` must still work for the demo —
 *      Cloudflare Pages SPA fallback hands the same route back, and our query
 *      cache rehydrates from the server.
 *
 * Fields are intentionally non-editable for the demo: the brief explicitly
 * says "show them, don't make them editable".
 *
 * Visual treatment: PoP1-inspired — chunky pixel-card on `pop-night`, mono
 * header strip, sand-dark row separators, torch-gold primary CTA. See the
 * wiki page for the canonical class list.
 *
 * Dokumenty section (#90):
 *   Renders the uploaded scan as an inline image preview, PDF block, or
 *   generic download row depending on `Contract.scan.mime_type`. Legacy
 *   contracts uploaded before migration 0006 have `scan_filename/mime/bytes`
 *   all null but `raw_scan_r2_key` set — they fall through to the generic
 *   download row with a "Sken smlouvy" placeholder name.
 */
import { Link, useNavigate, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Download, ExternalLink, FileText, Loader2, ShieldCheck } from 'lucide-react'
import { useContracts } from '@/hooks/queries'
import { useConfirmContract } from '@/hooks/queries'
import { AREA_ICON } from '@/components/pictograms'
import { useFamily } from '@/hooks/queries'
import type { Contract, ContractScan } from '@shared/schemas'

// ─── Helpers ──────────────────────────────────────────────────────────────────
/**
 * Converts a byte count to a human-readable string (e.g. "742 KB", "2.1 MB").
 * Returns null when bytes is null/unknown so callers can omit the size chip.
 */
function formatBytes(bytes: number | null): string | null {
  if (bytes == null) return null
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function ContractDetail() {
  const { id } = useParams<{ id: string }>()
  const contractsQuery = useContracts()
  const familyQuery = useFamily()
  const confirmContract = useConfirmContract()
  const navigate = useNavigate()

  const contract = contractsQuery.data?.find((c) => c.id === id)

  return (
    <main className="min-h-screen flex flex-col items-center justify-center px-4 py-10 bg-pop-night text-pop-cream">
      <Link to="/" className="pixel-btn-secondary absolute top-6 left-6 text-xs">
        ← Zpět
      </Link>

      <div className="w-full max-w-lg flex flex-col items-center gap-6">
        {contractsQuery.isLoading ? (
          <div className="pixel-card flex items-center gap-3 px-5 py-4 font-mono uppercase text-sm text-pop-silhouette">
            <Loader2 className="h-4 w-4 animate-spin text-pop-fire" strokeWidth={3} />
            <span>Načítám smlouvu…</span>
          </div>
        ) : !contract ? (
          <NotFound id={id} />
        ) : (
          <>
            <ContractCard
              contract={contract}
              holderName={
                familyQuery.data?.members.find((m) => m.id === contract.holder_member_id)
                  ?.name
              }
            />

            {/* Dokumenty section — between field rows card and action buttons */}
            <DocumentsSection scan={contract.scan ?? null} />

            <div className="w-full flex items-center justify-center gap-3">
              <button
                onClick={() =>
                  confirmContract.mutate(contract.id, {
                    onSuccess: () => navigate('/'),
                  })
                }
                disabled={confirmContract.isPending || contract.status === 'confirmed'}
                className="pixel-btn disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {confirmContract.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={3} />
                    Ukládám…
                  </>
                ) : (
                  <>
                    <ShieldCheck className="h-4 w-4" strokeWidth={2.5} />
                    Potvrdit
                  </>
                )}
              </button>
              <Link to="/" className="pixel-btn-secondary">
                Zrušit
              </Link>
            </div>
          </>
        )}
      </div>
    </main>
  )
}

// ─── Dokumenty section ────────────────────────────────────────────────────────
/**
 * Shows the uploaded scan in one of three states:
 *
 *   1. Image (mime starts `image/`) — inline thumbnail, click opens full-size
 *      in new tab; filename + human size below; Stáhnout button.
 *   2. PDF (`application/pdf`) — FileText icon, filename, size chip, Otevřít
 *      (new tab) + Stáhnout (`?download=1`) buttons.
 *   3. null — muted placeholder "K této smlouvě nebyl uložen sken."
 *
 * An unknown MIME type falls through to a minimal generic-download row.
 *
 * Visual treatment mirrors ContractCard: pixel-card, mono uppercase header
 * strip, sand-dark separators, torch-gold primary CTA.
 */
function DocumentsSection({ scan }: { scan: ContractScan | null }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, ease: [0, 0, 0, 1], delay: 0.06 }}
      className="pixel-card w-full overflow-hidden"
    >
      {/* Mono header strip — mirrors ContractCard header */}
      <div className="bg-pop-silhouette text-pop-cream font-mono uppercase text-xs px-3 py-1 tracking-wider border-b-[3px] border-pop-silhouette flex items-center">
        <span>Dokumenty</span>
      </div>

      <div className="p-4">
        {scan === null ? (
          <ScanMissing />
        ) : scan.mime_type?.startsWith('image/') ? (
          <ScanImage scan={scan} />
        ) : scan.mime_type === 'application/pdf' ? (
          <ScanPdf scan={scan} />
        ) : (
          // Legacy contracts (uploaded before migration 0006) have null
          // mime_type — fall through to the generic download row. The browser
          // will determine how to render based on the actual bytes.
          <ScanGeneric scan={scan} />
        )}
      </div>
    </motion.div>
  )
}

// ─── Scan state sub-components ───────────────────────────────────────────────

function ScanMissing() {
  return (
    <div className="flex items-center gap-3 py-1 text-pop-sand-dark">
      <FileText className="h-5 w-5 shrink-0 opacity-40" strokeWidth={2} />
      <span className="text-sm font-mono opacity-70">
        K této smlouvě nebyl uložen sken.
      </span>
    </div>
  )
}

function ScanImage({ scan }: { scan: ContractScan }) {
  const displayName = scan.filename ?? 'Sken smlouvy'
  const size = formatBytes(scan.bytes)
  return (
    <div className="flex flex-col gap-3">
      {/* Thumbnail — clicking opens full-size in a new tab */}
      <a
        href={scan.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Otevřít ${displayName} v plné velikosti`}
        className="block border-[3px] border-pop-silhouette overflow-hidden hover:brightness-105 transition-[filter] duration-100"
      >
        {/*
          `crossOrigin="use-credentials"` makes the browser include cookies on
          the cross-origin image fetch (pojisteno.cz → api.pojisteno.cz). The
          API CORS layer already returns Access-Control-Allow-Credentials: true
          and a specific Allow-Origin, which together satisfy the credentialed
          CORS contract for img elements. Without this attribute the browser
          sends the request without the session cookie and the endpoint
          returns 401, breaking the preview.
        */}
        <img
          src={scan.url}
          alt={displayName}
          crossOrigin="use-credentials"
          className="object-contain max-h-[300px] w-full"
          style={{ imageRendering: 'auto' }}
        />
      </a>

      {/* Filename + size row */}
      <div className="flex items-center justify-between border-t-[2px] border-pop-sand-dark pt-3 gap-3">
        <span
          className="text-xs font-mono text-pop-silhouette truncate"
          title={displayName}
        >
          {displayName}
        </span>
        {size && (
          <span className="text-[10px] font-mono uppercase tracking-wider text-pop-sand-dark shrink-0">
            {size}
          </span>
        )}
      </div>

      {/* Download */}
      <div className="flex justify-end">
        <a
          href={`${scan.url}?download=1`}
          className="pixel-btn-secondary text-xs"
          download
        >
          <Download className="h-3.5 w-3.5" strokeWidth={2.5} />
          Stáhnout
        </a>
      </div>
    </div>
  )
}

function ScanPdf({ scan }: { scan: ContractScan }) {
  const displayName = scan.filename ?? 'Sken smlouvy.pdf'
  const size = formatBytes(scan.bytes)
  return (
    <div className="flex flex-col gap-4">
      {/* Icon + metadata */}
      <div className="flex items-center gap-3">
        <div className="bg-pop-torch border-[3px] border-pop-silhouette p-2 shrink-0">
          <FileText className="h-6 w-6 text-pop-silhouette" strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex flex-col gap-0.5">
          <span
            className="text-sm font-semibold text-pop-silhouette truncate"
            title={displayName}
          >
            {displayName}
          </span>
          <span className="text-[10px] font-mono uppercase tracking-wider text-pop-sand-dark">
            PDF{size ? ` · ${size}` : ''}
          </span>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-3 border-t-[2px] border-pop-sand-dark pt-3">
        <a
          href={scan.url}
          target="_blank"
          rel="noopener noreferrer"
          className="pixel-btn text-xs"
        >
          <ExternalLink className="h-3.5 w-3.5" strokeWidth={2.5} />
          Otevřít
        </a>
        <a
          href={`${scan.url}?download=1`}
          className="pixel-btn-secondary text-xs"
          download
        >
          <Download className="h-3.5 w-3.5" strokeWidth={2.5} />
          Stáhnout
        </a>
      </div>
    </div>
  )
}

/**
 * Fallback for MIME types that are neither image/* nor application/pdf,
 * AND for legacy contracts (uploaded before migration 0006) where mime_type
 * is null — the user can still open / download the original scan.
 */
function ScanGeneric({ scan }: { scan: ContractScan }) {
  const displayName = scan.filename ?? 'Sken smlouvy'
  const size = formatBytes(scan.bytes)
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <div className="flex items-center gap-2 min-w-0">
        <FileText className="h-5 w-5 text-pop-silhouette shrink-0" strokeWidth={2.5} />
        <span
          className="text-sm font-mono text-pop-silhouette truncate"
          title={displayName}
        >
          {displayName}
        </span>
        {size && (
          <span className="text-[10px] font-mono uppercase tracking-wider text-pop-sand-dark shrink-0">
            {size}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <a
          href={scan.url}
          target="_blank"
          rel="noopener noreferrer"
          className="pixel-btn-secondary text-xs"
        >
          <ExternalLink className="h-3.5 w-3.5" strokeWidth={2.5} />
          Otevřít
        </a>
        <a
          href={`${scan.url}?download=1`}
          className="pixel-btn-secondary text-xs"
          download
        >
          <Download className="h-3.5 w-3.5" strokeWidth={2.5} />
          Stáhnout
        </a>
      </div>
    </div>
  )
}

// ─── Contract preview card ────────────────────────────────────────────────────
function ContractCard({
  contract,
  holderName,
}: {
  contract: Contract
  holderName?: string
}) {
  const Icon = AREA_ICON[contract.type] ?? FileText
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, ease: [0, 0, 0, 1] }}
      className="pixel-card w-full overflow-hidden"
    >
      {/* Mono header strip — matches Upload/Login for visual continuity. */}
      <div className="bg-pop-silhouette text-pop-cream font-mono uppercase text-xs px-3 py-1 tracking-wider border-b-[3px] border-pop-silhouette flex items-center justify-between">
        <span>Náhled smlouvy</span>
        <span className="opacity-70">#{contract.id}</span>
      </div>

      <div className="p-6 flex flex-col gap-5">
        <header className="flex items-center gap-3">
          <div className="bg-pop-torch border-[3px] border-pop-silhouette p-2">
            <Icon className="h-7 w-7 text-pop-silhouette" strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold uppercase tracking-wide text-pop-silhouette truncate">
              {contract.insurer}
            </h1>
            <div className="text-xs text-pop-shadow">{czechAreaLabel(contract.type)}</div>
          </div>
        </header>

        {/* Field rows — sand-dark separators between rows, cream-on-silhouette
            value chips for the canonical numeric/mono values. */}
        <dl className="flex flex-col">
          <FieldRow label="Pojistné" value={`${contract.premium_czk.toLocaleString('cs-CZ')} Kč / rok`} mono />
          <FieldRow label="Pojistník" value={holderName ?? contract.holder_member_id} />
          <FieldRow label="Platí od" value={contract.valid_from} mono />
          <FieldRow label="Platí do" value={contract.valid_to} mono />
          <FieldRow label="Typ" value={czechAreaLabel(contract.type)} />
          <FieldRow label="ID smlouvy" value={contract.id} mono />
        </dl>
      </div>
    </motion.div>
  )
}

function FieldRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between border-b-[2px] border-pop-sand-dark py-2 last:border-b-0 gap-3">
      <span className="text-[10px] font-mono uppercase tracking-wider text-pop-sand-dark shrink-0">
        {label}
      </span>
      <span
        className={`px-2 py-0.5 bg-pop-silhouette text-pop-cream truncate max-w-[60%] ${
          mono ? 'font-mono' : ''
        }`}
        title={value}
      >
        {value}
      </span>
    </div>
  )
}

function NotFound({ id }: { id: string | undefined }) {
  return (
    <div className="pixel-card flex flex-col items-center gap-3 px-8 py-6 text-center">
      <div className="bg-pop-sand-light border-[3px] border-pop-silhouette p-2">
        <FileText className="h-7 w-7 text-pop-silhouette" strokeWidth={2.5} />
      </div>
      <h1 className="text-lg font-semibold uppercase tracking-wide text-pop-silhouette">
        Smlouva nenalezena
      </h1>
      <p className="text-sm text-pop-shadow">
        Pro ID <span className="font-mono">{id ?? '—'}</span> nemáme záznam.
      </p>
    </div>
  )
}

/**
 * Czech display label for a contract type. Duplicated intentionally from
 * UploadFlow — these are two screens that may diverge as the design evolves,
 * and sharing a tiny lookup table cross-page would make the seam too tight.
 * Pull this up into a shared util only if a *third* surface needs it.
 */
function czechAreaLabel(code: string): string {
  switch (code) {
    case 'liability_personal':
      return 'Odpovědnost za škodu'
    case 'vehicle_mtpl':
      return 'Povinné ručení'
    case 'property_building':
      return 'Pojištění nemovitosti'
    case 'property_home':
      return 'Pojištění domácnosti'
    case 'travel':
      return 'Cestovní pojištění'
    case 'life':
      return 'Životní pojištění'
    default:
      return code
  }
}
